import { gql } from '@apollo/client';

// Query for fetching timeline data with pagination support
export const GET_SYSTEM_TIMELINE = gql`
  query getSystemTimeline(
    $system_id: String!
    $days: Int
    $limit: Int
    $alertsNextToken: String
    $alertReportsNextToken: String
    $shutdownNextToken: String
    $bootNextToken: String
    $networkNextToken: String
    $connectivityNextToken: String
    $batteryNextToken: String
    $pendantNextToken: String
    $pairingNextToken: String
  ) { 
    getSystem(system_id: $system_id) {
      system_id,
      advanced_info {
        alerts(days: $days, limit: $limit, nextToken: $alertsNextToken) {
          items {
            alert_id,
            cid_code,
            message,
            priority,
            status,
            timestamp_utc,
            type
          }
          nextToken
        }
        alert_reports(days: $days, limit: $limit, nextToken: $alertReportsNextToken) {
          items {
            type,
            timestamp_utc,
            callback_wait_time,
            callback_duration,
            endpoint,
            resolution,
            interface
          }
          nextToken
        }
        shutdown_notifications(days: $days, limit: $limit, nextToken: $shutdownNextToken) {
          items {
            timestamp_utc
          }
          nextToken
        }
        boot_notifications(days: $days, limit: $limit, nextToken: $bootNextToken) {
          items {
            timestamp_utc
          }
          nextToken
        }
        mobile_network_detailed_events(days: $days, limit: $limit, nextToken: $networkNextToken) {
          items {
            timestamp_utc,
            connectionStatus {
              lteStatus,
              wlanStatus,
              ethStatus
            },
            signalInfo {
              signalPower,
              signalQuality,
              rssi
            },
            networkInfo {
              imsRegistration,
              cid,
              activeBand,
              plmn,
              tac
            }
          }
          nextToken
        }
        connectivity_events(days: $days, limit: $limit, nextToken: $connectivityNextToken) {
          items {
            connection_state,
            timestamp_utc
          }
          nextToken
        }
        battery_soc_events(days: $days, limit: $limit, nextToken: $batteryNextToken) {
          items {
            state_of_charge,
            charge_remaining,
            dc_input_voltage,
            timestamp_utc
          }
          nextToken
        }
        pendant_events(days: $days, limit: $limit, nextToken: $pendantNextToken) {
          items {
            timestamp_utc,
            pendant_status {
              sensor_id,
              connected,
              battery_state_of_charge,
              zone,
              last_heartbeat_utc
            }
          }
          nextToken
        }
        sensor_pairing_events(days: $days, limit: $limit, nextToken: $pairingNextToken) {
          items {
            sensor_id,
            timestamp_utc
          }
          nextToken
        }
      }
    }
  }
`;

// Individual queries for pagination of specific data types
export const GET_ALERTS_PAGE = gql`
  query getAlertsPage($system_id: String!, $days: Int, $limit: Int, $nextToken: String) {
    getSystem(system_id: $system_id) {
      advanced_info {
        alerts(days: $days, limit: $limit, nextToken: $nextToken) {
          items {
            alert_id,
            cid_code,
            message,
            priority,
            status,
            timestamp_utc,
            type
          }
          nextToken
        }
      }
    }
  }
`;

export const GET_ALERT_REPORTS_PAGE = gql`
  query getAlertReportsPage($system_id: String!, $days: Int, $limit: Int, $nextToken: String) {
    getSystem(system_id: $system_id) {
      advanced_info {
        alert_reports(days: $days, limit: $limit, nextToken: $nextToken) {
          items {
            type,
            timestamp_utc,
            callback_wait_time,
            callback_duration,
            endpoint,
            resolution,
            interface
          }
          nextToken
        }
      }
    }
  }
`;

export const GET_SHUTDOWN_PAGE = gql`
  query getShutdownPage($system_id: String!, $days: Int, $limit: Int, $nextToken: String) {
    getSystem(system_id: $system_id) {
      advanced_info {
        shutdown_notifications(days: $days, limit: $limit, nextToken: $nextToken) {
          items {
            timestamp_utc
          }
          nextToken
        }
      }
    }
  }
`;

export const GET_BOOT_PAGE = gql`
  query getBootPage($system_id: String!, $days: Int, $limit: Int, $nextToken: String) {
    getSystem(system_id: $system_id) {
      advanced_info {
        boot_notifications(days: $days, limit: $limit, nextToken: $nextToken) {
          items {
            timestamp_utc
          }
          nextToken
        }
      }
    }
  }
`;

export const GET_NETWORK_PAGE = gql`
  query getNetworkPage($system_id: String!, $days: Int, $limit: Int, $nextToken: String) {
    getSystem(system_id: $system_id) {
      advanced_info {
        mobile_network_detailed_events(days: $days, limit: $limit, nextToken: $nextToken) {
          items {
            timestamp_utc,
            connectionStatus {
              lteStatus,
              wlanStatus,
              ethStatus
            },
            signalInfo {
              signalPower,
              signalQuality,
              rssi
            },
            networkInfo {
              imsRegistration,
              cid,
              activeBand,
              plmn,
              tac
            }
          }
          nextToken
        }
      }
    }
  }
`;

export const GET_CONNECTIVITY_PAGE = gql`
  query getConnectivityPage($system_id: String!, $days: Int, $limit: Int, $nextToken: String) {
    getSystem(system_id: $system_id) {
      advanced_info {
        connectivity_events(days: $days, limit: $limit, nextToken: $nextToken) {
          items {
            connection_state,
            timestamp_utc
          }
          nextToken
        }
      }
    }
  }
`;

export const GET_BATTERY_PAGE = gql`
  query getBatteryPage($system_id: String!, $days: Int, $limit: Int, $nextToken: String) {
    getSystem(system_id: $system_id) {
      advanced_info {
        battery_soc_events(days: $days, limit: $limit, nextToken: $nextToken) {
          items {
            state_of_charge,
            charge_remaining,
            dc_input_voltage,
            timestamp_utc
          }
          nextToken
        }
      }
    }
  }
`;

export const GET_PENDANT_PAGE = gql`
  query getPendantPage($system_id: String!, $days: Int, $limit: Int, $nextToken: String) {
    getSystem(system_id: $system_id) {
      advanced_info {
        pendant_events(days: $days, limit: $limit, nextToken: $nextToken) {
          items {
            timestamp_utc,
            pendant_status {
              sensor_id,
              connected,
              battery_state_of_charge,
              zone,
              last_heartbeat_utc
            }
          }
          nextToken
        }
      }
    }
  }
`;

export const GET_PAIRING_PAGE = gql`
  query getPairingPage($system_id: String!, $days: Int, $limit: Int, $nextToken: String) {
    getSystem(system_id: $system_id) {
      advanced_info {
        sensor_pairing_events(days: $days, limit: $limit, nextToken: $nextToken) {
          items {
            sensor_id,
            timestamp_utc
          }
          nextToken
        }
      }
    }
  }
`;

